import { giphyGreen } from '@giphy/js-brand'
import { IGif } from '@giphy/js-types'
import { Gif } from '@giphy/react-components'
import download from 'downloadjs'
import { SyntheticEvent, useContext } from 'react'
import sanitize from 'sanitize-filename'
import { URL_TYPE } from 'shared/components/inputs/url-input'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import log from 'shared/util/log'
import urlUtils from 'shared/util/url-utils'
import styled from 'styled-components'
import { ZoomMakerWelcomeContext } from './../context'

export const STARTER_WIDTH = 340
export const STARTER_HEIGHT = 190

const DownloadButton = styled.div`
    background: rgba(18, 18, 18, 0.75);
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
    height: 34px;
    left: 10px;
    line-height: 36px;
    opacity: 0;
    position: absolute;
    text-align: center;
    top: 10px;
    transition: opacity 0.2s linear;
    width: 34px;

    &:hover {
        background: ${giphyGreen};
        color: #000;
    }
`

const Container = styled.div`
    border-radius: 2px;
    cursor: pointer;
    overflow: hidden;
    position: relative;

    &:hover {
        box-shadow: 0 0 0 2px ${giphyGreen};

        ${DownloadButton} {
            opacity: 1;
        }
    }
`

const getVideoUrl = ({ images = {} }: any) => {
    if (images.hd) {
        return images.hd.mp4
    }
    if (images.source?.url.indexOf('.mp4') > -1 || images.source?.url.indexOf('.mov') > -1) {
        return images.source.url
    }
    return images.original_mp4.mp4
}

const gifWithBackdropTitle = (gif: IGif) => {
    const { title } = gif
    return { ...gif, title: title.replace('GIF', 'Zoom Virtual Background') }
}

type Props = {
    className?: string
    gif: IGif
    downloadOnly?: boolean
}

const ZoomMakerStarterItem = ({ className, gif, downloadOnly }: Props) => {
    const { onPreviewReady } = useContext(ZoomMakerWelcomeContext)
    const videoUrl = getVideoUrl(gif).replace(/media(\d?).giphy.com/, 'i.giphy.com')

    const trackEvent = (action: string) => {
        const category = 'Create Backdrop'
        const label = `${gif.id}`
        log.info(`${category} GA Event - action: ${action}, label: ${label}`)
        googleAnalyticsEvent({ category, action, label })
    }

    const onDownload = async () => {
        const result = await fetch(videoUrl)
        const blob = await result.blob()
        trackEvent('Download')
        download(blob, sanitize(`${gif.title}-backdrop.mp4`))
    }

    const onClick = (gif: IGif, e: SyntheticEvent) => {
        if ((e.target as HTMLElement).tagName === 'IMG') {
            if (downloadOnly) {
                window.open(gif.url)
            } else {
                const fileType = urlUtils.mediaType(videoUrl)
                trackEvent('Starter Selected')
                onPreviewReady([
                    {
                        type: 'url',
                        fileType,
                        urlType: URL_TYPE.VIDEO,
                        sourceUrl: videoUrl,
                        file: videoUrl,
                        preview: videoUrl,
                        videoEmbed: videoUrl,
                        id: `${gif.id}`,
                        isStarter: true,
                    },
                ])
            }
        }
    }

    return (
        <Container className={className}>
            <Gif
                gif={gifWithBackdropTitle(gif)}
                height={STARTER_HEIGHT}
                width={STARTER_WIDTH}
                onGifClick={onClick}
                noLink
            />
            <DownloadButton className="ss-download" onClick={onDownload} />
        </Container>
    )
}

export default ZoomMakerStarterItem
